/**
 * Total ordering for every comparator in the engine.
 *
 * The reproducibility guarantee — same inputs ⇒ byte-identical plan — rests
 * entirely on the sorts being TOTAL. Today they are total only by accident: the
 * candidate sort breaks ties on `title`, and that works solely because all 51
 * catalog titles happen to be distinct. Once the composer is parameterised, ties
 * stop being rare — a flat ranking denominator, or an HR user setting the four
 * origin weights equal, makes them the common case, and the tiebreak then
 * carries the entire ordering.
 *
 * So every comparator here ends in an id comparison, and ids are compared
 * byte-wise rather than by locale. A title is localisable and a catalog import
 * could collide two of them; an id is the primary key.
 */

/**
 * One shared collator instead of `localeCompare(x, "de")` at each call site.
 *
 * Two reasons, and the second is the dangerous one. It is 3–5× faster, which
 * matters inside a loop over 1,960 employees. And `String.prototype.localeCompare`
 * resolves its locale per call against whatever ICU the runtime was built with:
 * a Node compiled with `small-icu` — some slim container bases are — collates
 * "Ö" differently and silently reorders the plan. Pinning one collator makes
 * that a single, testable decision.
 */
export const DE_COLLATOR = new Intl.Collator("de", { sensitivity: "variant" })

/** Compare display text in German collation order. */
export function byText(a: string, b: string): number {
    return DE_COLLATOR.compare(a, b)
}

/** Compare ids byte-wise. Locale-independent by design — see the header. */
export function byId(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0
}

/**
 * Scores are compared as integers.
 *
 * Two mathematically equal scores can differ by ~1e-15 once a weight is not
 * exactly representable (an HR user typing `33.3` is enough), which turns a real
 * tie into a phantom ordering that depends on floating-point noise rather than
 * on the tiebreak. Quantising first makes the tie visible so the id tiebreak can
 * resolve it deterministically.
 *
 * 1e9 is safe: the largest achievable score is ~1600 (weight 100 × gap 4 ÷ 0.5 h
 * minimum duration), so the product stays below 2^53 with room to spare.
 *
 * A non-finite score is mapped to 0 rather than propagated. A comparator that
 * returns NaN is treated as 0 by V8, which makes the resulting order an artefact
 * of the sort algorithm and therefore length-dependent — a nondeterminism that
 * no test would reliably catch.
 */
export const SCORE_QUANTUM = 1e9

export function scoreKey(score: number): number {
    return Number.isFinite(score) ? Math.round(score * SCORE_QUANTUM) : 0
}
